import { Mail } from 'lucide-react';
import { MailSenderResponseDto } from '../types/response';

export interface EmailLivePreviewProps {
  sender: MailSenderResponseDto;
  subject: string;
  isLoadingTemplate: boolean;
  renderedTemplate: string;
  selectedRecipientsCount?: number;
}

export const EmailLivePreview = ({ sender, subject, isLoadingTemplate, renderedTemplate, selectedRecipientsCount = 0 }: EmailLivePreviewProps) => {
  return (
    <div className="flex flex-col bg-white flex-grow">
      <div
        className="h-8 flex items-center px-4 text-white text-sm font-medium flex-shrink-0"
        style={{
          background: `
                radial-gradient(circle at 10% 10%, rgba(95, 213, 222, 0.3) 0%, transparent 40%),
                radial-gradient(circle at 90% 90%, rgba(192, 222, 96, 0.3) 0%, transparent 40%),
                linear-gradient(135deg, #5FD5DE 0%, #C0DE60 100%)
              `
        }}>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-white/30 rounded-full"></div>
          <div className="w-3 h-3 bg-white/30 rounded-full"></div>
          <div className="w-3 h-3 bg-white/30 rounded-full"></div>
        </div>
        <div className="flex-1 text-center">
          <div className="flex items-center justify-center gap-2">
            <div className="truncate max-w-xs">{subject || 'E-Mail Vorschau'}</div>
            <span className="text-white/80">•</span>
            <span className="text-white/80 whitespace-nowrap">{sender.fromEmail || '...'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 opacity-0">
          <div className="w-3 h-3 bg-white/30 rounded-full"></div>
          <div className="w-3 h-3 bg-white/30 rounded-full"></div>
          <div className="w-3 h-3 bg-white/30 rounded-full"></div>
        </div>
      </div>

      <div className="h-10 bg-gray-50 border-b border-gray-200 flex items-center px-4 gap-4 text-sm flex-shrink-0">
        <div className="flex items-center gap-4 text-gray-600">
          <span className="flex items-center gap-1">🗑️ Löschen</span>
          <span className="flex items-center gap-1">📁 Archivieren</span>
          <span className="flex items-center gap-1">⚠️ Melden</span>
          <span className="flex items-center gap-1">📂 Verschieben</span>
        </div>
        <div className="ml-auto flex items-center gap-2 text-gray-600">
          <span>Copilot</span>
          <span>▼</span>
        </div>
      </div>

      <div className="flex flex-col p-4 flex-grow gap-0">
        <div className="mb-4 flex-shrink-0">
          <h1 className="font-semibold text-gray-900 mb-1">{subject || '...'}</h1>
        </div>
        <div className="mb-4 flex-shrink-0">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-[#5FD5DE] to-[#C0DE60] rounded-full flex items-center justify-center text-white font-bold">
              7
            </div>
            <div className="flex-1">
              <div className="font-semibold text-gray-900">
                Von: {sender.fromName || '...'} <span className="text-gray-500">&lt;{sender.fromEmail || '...'}&gt;</span>
              </div>
              <div className="text-sm text-gray-600">An: Kundenliste</div>
              <div className="text-sm text-blue-600 font-medium">📧 {selectedRecipientsCount} Empfänger ausgewählt</div>
              <div className="text-sm text-gray-500">Heute um {new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg border border-gray-200 flex-grow flex flex-col">
          <div className="p-0 flex-grow flex flex-col">
            {isLoadingTemplate ? (
              <div className="text-center py-8 flex-grow flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Template wird gerendert...</p>
              </div>
            ) : renderedTemplate ? (
              <div className="w-full flex flex-col h-full">
                <EmailIFramePreview renderedTemplate={renderedTemplate} />
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400 flex-grow flex flex-col items-center justify-center">
                <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Geben Sie eine Nachricht ein, um die Vorschau zu sehen</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const EmailIFramePreview = ({ renderedTemplate }: { renderedTemplate: string }) => {
  return (
    <div className="w-full flex flex-col h-full">
      <iframe
        title="mail-preview"
        srcDoc={renderedTemplate}
        className="w-full border-0"
        style={{ height: '100%', maxHeight: 'none' }}
        sandbox="allow-same-origin"
      />
    </div>
  );
};
